import { parseSalaryValue } from "@/helpers/utils.helper.ts";
import i18n from "@/i18n.ts";
import { z } from "zod";
import { ApiField, ApiFieldTypes } from "@/types/custom-field.types.ts";

interface AdditionalCompensationSchemaOptions {
  isMandatory?: (type: ApiFieldTypes) => boolean;
  hasField?: (type: ApiFieldTypes) => boolean;
  customFields?: ApiField[];
  getZodSchemaForField?: (field: ApiField) => Record<string, z.ZodTypeAny>;
}

const additionalCompensationAmountSchema = z
  .union([z.string(), z.number(), z.null(), z.undefined()])
  .transform((value) => parseSalaryValue(value))
  .refine((value) => value === null || !Number.isNaN(value), {
    message: i18n.t("errors:field_number_required"),
  })
  .refine((value) => value === null || Number.isNaN(value) || value >= 0, {
    message: i18n.t("errors:value_cannot_be_negative"),
  });

const buildAdditionalCompensationAmountsSchema = (hasField: (type: ApiFieldTypes) => boolean) =>
  z
    .object({
      amount_gross: additionalCompensationAmountSchema,
      amount_net: additionalCompensationAmountSchema,
    })
    .superRefine((value, context) => {
      const visibleFields = (
        [
          ["amount_gross", ApiFieldTypes.employee_additional_compensation_amount_gross],
          ["amount_net", ApiFieldTypes.employee_additional_compensation_amount_net],
        ] as const
      ).filter(([, type]) => hasField(type));
      if (visibleFields.length === 0) return;
      if (visibleFields.some(([field]) => value[field] !== null)) return;

      visibleFields.forEach(([field]) =>
        context.addIssue({
          code: "custom",
          path: [field],
          message: i18n.t("errors:additional_compensation_amount_required"),
        })
      );
    });

export const buildEmployeeAdditionalCompensationSchema = ({
  isMandatory = () => false,
  hasField = () => true,
  customFields = [],
  getZodSchemaForField = () => ({}),
}: AdditionalCompensationSchemaOptions = {}) =>
  z.intersection(
    z.object({
      compensation_type_id: isMandatory(
        ApiFieldTypes.employee_additional_compensation_compensation_type
      )
        ? z.number({ error: i18n.t("errors:field_required") })
        : z.number().nullish(),
      description: isMandatory(ApiFieldTypes.employee_additional_compensation_description)
        ? z
            .string({ error: i18n.t("errors:field_required") })
            .min(1, i18n.t("errors:field_required"))
        : z.string().nullish(),
      date_effective_from: z.date({ error: i18n.t("errors:field_required") }),
      currency: isMandatory(ApiFieldTypes.employee_additional_compensation_currency)
        ? z
            .string({ error: i18n.t("errors:field_required") })
            .min(1, i18n.t("errors:field_required"))
        : z.string().nullish(),
      comment: isMandatory(ApiFieldTypes.employee_additional_compensation_comment)
        ? z
            .string({ error: i18n.t("errors:field_required") })
            .min(1, i18n.t("errors:field_required"))
        : z.string().nullish(),
      ...Object.assign({}, ...customFields.map((field) => getZodSchemaForField(field))),
    }),
    buildAdditionalCompensationAmountsSchema(hasField)
  );
